/* Métrica · book viewer (v3, 30-sep-2026) — the «Look inside» for spiral notebooks that open as spreads.
 * Data: mtr-book-<slug>.json (cuaderno-pentagramado/_look-inside.mjs). Pages are the printer's files, served by the
 * Shopify CDN at the width each view needs. Spreads side by side with the white spiral between them; one page at a time
 * on phones; a strip with every spread; deep zoom (wheel, pinch, drag, double tap); the text of a page to read; and the
 * page at actual size, once the screen is calibrated with a bank card.
 */
(() => {
  if (customElements.get('metrica-book-viewer')) return;
  const el = (tag, cls, attrs = {}) => { const e = document.createElement(tag); if (cls) e.className = cls; for (const [k, v] of Object.entries(attrs)) k === 'text' ? (e.textContent = v) : e.setAttribute(k, v); return e; };
  const cdn = (src, w) => src.startsWith('/') ? src : src + (src.includes('?') ? '&' : '?') + 'width=' + w;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const CARD = { w: 85.6, h: 53.98 };                      // ISO/IEC 7810 ID-1: every bank card
  const KEY = 'mtr-px-per-mm';
  const readPxmm = () => { try { const v = +localStorage.getItem(KEY); return v > 1 && v < 20 ? v : null; } catch { return null; } };
  const savePxmm = v => { try { localStorage.setItem(KEY, String(v)); } catch {} };

  // the white Wire-O (Prodigi: white, 3:1 pitch = a loop every 8.47 mm, 23 on the 210 mm edge). Each loop is a DOUBLE
  // wire through a round hole punched in the paper, so the drawing reaches `ov` px onto the page(s) it binds.
  // mode: 'middle' (between two pages), 'left' (on the left edge of one page), 'right'
  // n: loops on the bound edge — 23 on the 210 mm edge (landscape), 35 on the 297 mm edge (portrait); the JSON says `loops`
  function spiral(h, gap, mode, n = 23) {
    const pitch = h / n, ov = Math.max(5, gap * 0.55), W = mode === 'middle' ? gap + 2 * ov : gap + ov;
    const r = Math.min(pitch * 0.17, ov * 0.34), bh = Math.max(1.1, pitch * 0.1), sep = pitch * 0.13;
    const holes = mode === 'middle' ? [ov * 0.5, W - ov * 0.5] : mode === 'left' ? [W - ov * 0.5] : [ov * 0.5];
    const x0 = mode === 'right' ? holes[0] : mode === 'left' ? 0 : holes[0], x1 = mode === 'left' ? holes[0] : mode === 'right' ? W : holes[1];
    let g = '';
    for (let i = 0; i < n; i++) {
      const y = (i + 0.5) * pitch;
      for (const hx of holes) g += `<circle cx="${hx}" cy="${y}" r="${r}" fill="#3a332c"/>`;
      for (const d of [-sep, sep]) g += `<rect x="${x0}" y="${y + d - bh / 2}" width="${x1 - x0}" height="${bh}" rx="${bh / 2}" fill="#fdfdfb" stroke="#a8a49c" stroke-width=".6"/>`;
    }
    const s = el('span', 'mbv-spiral');
    s.style.width = W + 'px'; s.style.height = h + 'px';
    s.style.margin = mode === 'middle' ? `0 ${-ov}px` : mode === 'left' ? `0 ${-ov}px 0 0` : `0 0 0 ${-ov}px`;
    s.innerHTML = `<svg width="${W}" height="${h}" viewBox="0 0 ${W} ${h}" aria-hidden="true"><g filter="url(#mbvsh)">${g}</g>
      <defs><filter id="mbvsh" x="-10%" y="-10%" width="120%" height="120%"><feDropShadow dx="0" dy="${Math.max(.6, bh * .5)}" stdDeviation="${Math.max(.4, bh * .4)}" flood-color="#2a211b" flood-opacity=".28"/></filter></defs></svg>`;
    return s;
  }

  class BookViewer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return; this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.querySelector('.mbv-trigger').addEventListener('click', () => this.open());
      this.mq = matchMedia('(max-width: 760px)');
      this.onMq = () => { if (this.data) { this.buildSequence(this.currentSpread()); this.render(0); this.buildStrip(); } };
      this.mq.addEventListener('change', this.onMq);
      this.onResize = () => { if (this.data && this.dialog.open) { this.layout(); if (this.zoomPage) this.fit(true); } };
      addEventListener('resize', this.onResize);
    }
    disconnectedCallback() { this.mq?.removeEventListener('change', this.onMq); removeEventListener('resize', this.onResize); }

    /* ── shell ── */
    build() {
      const d = this.dialog;
      this.papersEl = el('div', 'mbv-papers', { role: 'group', 'aria-label': 'Paper' });
      d.querySelector('.mbv-head').insertBefore(this.papersEl, d.querySelector('.mbv-close'));
      d.querySelector('.mbv-close').addEventListener('click', () => d.close());
      this.stage = el('div', 'mbv-stage');
      this.prev = el('button', 'mbv-arrow mbv-prev', { type: 'button', 'aria-label': 'Previous pages' }); this.prev.innerHTML = '<span aria-hidden="true">←</span>';
      this.next = el('button', 'mbv-arrow mbv-next', { type: 'button', 'aria-label': 'Next pages' }); this.next.innerHTML = '<span aria-hidden="true">→</span>';
      this.caption = el('div', 'mbv-caption', { 'aria-live': 'polite' });
      const bar = el('div', 'mbv-bar'); bar.append(this.prev, this.caption, this.next);
      this.strip = el('nav', 'mbv-strip', { 'aria-label': 'Every spread in the notebook' });
      this.note = el('p', 'mbv-note');
      // the zoom layer
      this.zoom = el('section', 'mbv-zoom', { 'aria-label': 'Page up close' }); this.zoom.hidden = true;
      const tools = el('div', 'mbv-ztools');
      this.zBack = el('button', 'mbv-zback', { type: 'button', text: '← All pages' });
      this.zLabel = el('span', 'mbv-zlabel');
      this.zRead = el('button', 'mbv-zbtn', { type: 'button', text: 'Read the text' });
      this.zActual = el('button', 'mbv-zbtn', { type: 'button', 'aria-pressed': 'false', text: 'At actual size' });
      this.zOut = el('button', 'mbv-zbtn mbv-zicon', { type: 'button', 'aria-label': 'Zoom out', text: '−' });
      this.zIn = el('button', 'mbv-zbtn mbv-zicon', { type: 'button', 'aria-label': 'Zoom in', text: '+' });
      tools.append(this.zBack, this.zLabel, this.zRead, this.zActual, this.zOut, this.zIn);
      this.zView = el('div', 'mbv-zview', { tabindex: '0', 'aria-label': 'Page image. Drag to move, pinch or scroll to zoom.' });
      this.zImg = el('img', 'mbv-zimg', { alt: '', draggable: 'false' }); this.zView.append(this.zImg);
      this.zCap = el('p', 'mbv-zcap', { 'aria-live': 'polite' });
      this.read = el('article', 'mbv-read', { tabindex: '-1' }); this.read.hidden = true;
      this.calib = el('div', 'mbv-calib', { role: 'dialog', 'aria-label': 'Calibrate your screen' }); this.calib.hidden = true;
      this.zoom.append(tools, this.zView, this.zCap, this.read, this.calib);
      d.querySelector('.mbv-body').append(this.stage, bar, this.note, this.strip, this.zoom);

      this.prev.addEventListener('click', () => this.move(-1));
      this.next.addEventListener('click', () => this.move(1));
      this.zBack.addEventListener('click', () => this.closeZoom());
      this.zRead.addEventListener('click', () => this.openRead());
      this.zActual.addEventListener('click', () => this.actualSize());
      this.zIn.addEventListener('click', () => this.zoomBy(1.5));
      this.zOut.addEventListener('click', () => this.zoomBy(1 / 1.5));
      d.addEventListener('close', () => { document.body.classList.remove('mbv-open'); this.closeZoom(true); this.querySelector('.mbv-trigger').focus({ preventScroll: true }); });
      d.addEventListener('cancel', e => {                  // Esc closes the innermost layer first
        if (!this.calib.hidden) { e.preventDefault(); this.calib.hidden = true; }
        else if (!this.read.hidden) { e.preventDefault(); this.closeRead(); }
        else if (!this.zoom.hidden) { e.preventDefault(); this.closeZoom(); }
      });
      d.addEventListener('keydown', e => {
        if (!this.zoom.hidden) { if (e.key === '+' || e.key === '=') this.zoomBy(1.4); if (e.key === '-') this.zoomBy(1 / 1.4); return; }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); this.move(e.key === 'ArrowRight' ? 1 : -1); }
      });
      d.addEventListener('click', e => { if (e.target === d) d.close(); });
      // swipe between spreads
      let sx = null;
      this.stage.addEventListener('pointerdown', e => { sx = e.pointerType === 'mouse' ? null : e.clientX; });
      this.stage.addEventListener('pointerup', e => { if (sx != null && Math.abs(e.clientX - sx) > 45) this.move(e.clientX < sx ? 1 : -1); sx = null; });
      this.gestures();
    }

    async open() {
      if (this.dialog.open) return;
      if (!this.built) { this.build(); this.built = true; }
      this.dialog.showModal(); document.body.classList.add('mbv-open');
      if (!this.data) await this.load(); else this.layout();
      const want = new URLSearchParams(location.search).get('variant');       // the paper chosen on the product page
      const p = this.data?.papers.find(x => String(x.variant) === want);
      if (p && p.id !== this.paper?.id) this.setPaper(p.id);
    }
    async load() {
      this.stage.replaceChildren(el('p', 'mbv-status', { role: 'status', text: 'Opening the notebook…' }));
      try {
        const r = await fetch(this.dataset.src); if (!r.ok) throw 0;
        this.data = await r.json();
        this.note.textContent = this.data.note || '';
        this.papersEl.replaceChildren(...this.data.papers.map(p => { const b = el('button', 'mbv-paper', { type: 'button', 'aria-pressed': 'false', text: p.label }); b.dataset.paper = p.id; b.addEventListener('click', () => this.setPaper(p.id)); return b; }));
        this.papersEl.hidden = this.data.papers.length < 2;
        // it opens where the product is: the first spread of the score (data.start = a folio), or the second spread
        const st = this.data.papers[0].spreads.findIndex(s => s.pages.some(p => p?.folio === this.data.start));
        this.setPaper(this.data.papers[0].id, st >= 0 ? st : 1);
      } catch {
        const s = el('p', 'mbv-status', { role: 'status', text: 'The preview could not load. ' });
        const b = el('button', 'mbv-retry', { type: 'button', text: 'Try again' }); b.onclick = () => this.load(); s.append(b);
        this.stage.replaceChildren(s);
      }
    }
    setPaper(id, spread = this.currentSpread() ?? 2) {
      this.paper = this.data.papers.find(p => p.id === id);
      this.papersEl.querySelectorAll('[data-paper]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.paper === id)));
      this.buildSequence(spread); this.buildStrip(); this.render(0);
    }
    // desktop: one step = one spread; phone: one step = one page
    buildSequence(spread) {
      const sp = this.paper.spreads;
      this.seq = this.mq.matches ? sp.flatMap((s, i) => s.pages.map((p, j) => p ? { spread: i, only: j } : null).filter(Boolean)) : sp.map((s, i) => ({ spread: i }));
      this.index = Math.max(0, this.seq.findIndex(x => x.spread === spread));
    }
    currentSpread() { return this.seq?.[this.index]?.spread; }
    move(d) {
      const to = this.index + d; if (!this.seq || to < 0 || to >= this.seq.length) return;
      this.index = to; this.render(d);
    }

    /* ── the spread ── */
    render(dir) {
      const step = this.seq[this.index], s = this.paper.spreads[step.spread];
      const shown = step.only != null ? [s.pages[step.only]] : s.pages;
      const box = el('div', 'mbv-spread');
      this.cells = shown.map(p => {
        if (!p) return el('span', 'mbv-blank', { 'aria-hidden': 'true' });
        const b = el('button', 'mbv-page', { type: 'button', 'aria-label': `${p.label}${p.folio ? ', page ' + p.folio : ''}. Open up close` });
        const im = this.data.pages[p.id], img = el('img', '', { alt: p.label, width: im.w, height: im.h, decoding: 'async' });
        img.src = cdn(im.src, 1400); img.srcset = [700, 1100, 1600, 2200].map(w => `${cdn(im.src, w)} ${w}w`).join(', ');
        b.append(img); b.addEventListener('click', () => this.openZoom(p)); b._img = img; return b;
      });
      box.append(...this.cells);
      box.dataset.single = String(shown.length === 1);
      // where the spiral goes: between two pages; on a single page, on its bound edge (a right-hand page and the front
      // cover are bound on the left; a left-hand page and the back cover, seen from behind, on the right)
      const one = shown[0];
      box.dataset.bind = shown.length === 2 ? 'middle' : one.folio != null ? (one.folio % 2 ? 'left' : 'right') : s.section === 'Back cover' ? 'right' : 'left';
      this.stage.replaceChildren(box); this.spreadEl = box;
      if (dir && !reduce.matches) box.animate([{ opacity: 0, transform: `translateX(${dir * 22}px)` }, { opacity: 1, transform: 'none' }], { duration: 260, easing: 'cubic-bezier(.2,.7,.2,1)' });
      this.layout();
      // caption
      const pgs = s.pages.filter(Boolean), f = pgs.map(p => p.folio).filter(Boolean);
      const where = s.range ? `Pages ${s.range[0]}–${s.range[1]}` : f.length ? (f.length > 1 ? `Pages ${f[0]}–${f.at(-1)}` : `Page ${f[0]}`) : '';
      // the labels, each fact once («Leaf 1 verso · Leaf 2 recto · Allegro spiritoso», not the movement twice)
      const labels = [...new Set((step.only != null ? [s.pages[step.only]] : pgs).flatMap(p => p.label.split(' · ')))];
      const strong = el('strong', '', { text: s.section });
      this.caption.replaceChildren(strong, el('span', '', { text: [where, labels.join(' · ')].filter(Boolean).join(' · ') }));
      this.prev.disabled = this.index === 0; this.next.disabled = this.index === this.seq.length - 1;
      this.strip.querySelectorAll('.mbv-thumb').forEach((t, i) => { const on = i === step.spread; t.setAttribute('aria-current', on ? 'true' : 'false'); if (on) t.scrollIntoView({ block: 'nearest', inline: 'center', behavior: dir && !reduce.matches ? 'smooth' : 'auto' }); });
      // the next and previous spreads, ahead of time
      for (const k of [this.index + 1, this.index - 1]) { const q = this.seq[k]; if (!q) continue; for (const p of this.paper.spreads[q.spread].pages) if (p) { const i = new Image(); i.src = cdn(this.data.pages[p.id].src, 1400); } }
    }
    layout() {
      if (!this.spreadEl) return;
      const r = this.stage.getBoundingClientRect(), a = this.data.page.w / this.data.page.h;
      const pad = this.mq.matches ? 8 : 28, W = r.width - 2 * pad, H = r.height - 2 * pad;
      const n = this.cells.length, gapK = 0.045;                       // the spiral takes 4.5 % of a page width
      const pw = Math.max(40, Math.min(H * a, W / (n + gapK))), ph = pw / a, gw = Math.max(10, pw * gapK);
      this.spreadEl.style.setProperty('--pw', pw + 'px'); this.spreadEl.style.setProperty('--ph', ph + 'px');
      this.cells.forEach(c => { if (c._img) c._img.sizes = Math.round(pw) + 'px'; });
      this.spreadEl.querySelector('.mbv-spiral')?.remove();
      const bind = this.spreadEl.dataset.bind, sp = spiral(ph, gw, bind, this.data.loops || 23);
      if (bind === 'middle') this.cells[0].after(sp); else if (bind === 'left') this.spreadEl.prepend(sp); else this.spreadEl.append(sp);
    }

    /* ── the strip: every spread, labelled by section ── */
    buildStrip() {
      let last = '';
      const items = this.paper.spreads.map((s, i) => {
        const t = el('button', 'mbv-thumb', { type: 'button', 'aria-label': `${s.section}${s.pages.some(p => p?.folio) ? ', pages ' + s.pages.filter(p => p?.folio).map(p => p.folio).join('–') : ''}` });
        if (s.section !== last) { t.dataset.section = s.section; last = s.section; }
        for (const p of s.pages) { if (!p) { t.append(el('span', 'mbv-tblank')); continue; } const im = el('img', '', { alt: '', loading: 'lazy', src: cdn(this.data.pages[p.id].src, 220), width: 110, height: 78 }); t.append(im); }
        t.addEventListener('click', () => { const k = this.seq.findIndex(x => x.spread === i); if (k >= 0) { const d = Math.sign(k - this.index); this.index = k; this.render(d); } });
        return t;
      });
      this.strip.replaceChildren(...items);
    }

    /* ── up close ── */
    openZoom(p) {
      this.zoomPage = p; this.zoom.hidden = false; this.actual = false; this.zActual.setAttribute('aria-pressed', 'false');
      const im = this.data.pages[p.id];
      this.zLabel.textContent = p.folio ? `${p.label} · page ${p.folio}` : p.label;
      this.zRead.hidden = !p.read;
      this.nat = { w: im.w, h: im.h };
      this.zImg.style.width = im.w + 'px'; this.zImg.style.height = im.h + 'px';
      this.zImg.alt = p.label;
      this.zImg.src = cdn(im.src, 1600);
      const hi = new Image(); hi.onload = () => { if (this.zoomPage === p) this.zImg.src = hi.src; }; hi.src = im.src;   // the printer's file, full size
      this.zCap.textContent = 'Scroll or pinch to zoom · drag to move · double-tap to enlarge';
      requestAnimationFrame(() => { this.fit(); this.zView.focus({ preventScroll: true }); });
    }
    closeZoom(silent) {
      if (this.zoom.hidden) return;
      this.zoom.hidden = true; this.read.hidden = true; this.calib.hidden = true; this.zoomPage = null;
      if (!silent) (this.stage.querySelector('.mbv-page') || this.stage).focus({ preventScroll: true });
    }
    fit(keep) {
      const v = this.zView.getBoundingClientRect();
      this.kFit = Math.min(v.width / this.nat.w, v.height / this.nat.h) * 0.94;
      if (keep && this.actual) return this.actualSize(true);
      this.set(this.kFit, (v.width - this.nat.w * this.kFit) / 2, (v.height - this.nat.h * this.kFit) / 2);
    }
    set(k, x, y, anim) {
      const v = this.zView.getBoundingClientRect(), max = Math.max(this.kFit * 8, 1.2);
      k = Math.min(max, Math.max(this.kFit * 0.9, k));
      const w = this.nat.w * k, h = this.nat.h * k;
      x = w <= v.width ? (v.width - w) / 2 : Math.min(0, Math.max(v.width - w, x));
      y = h <= v.height ? (v.height - h) / 2 : Math.min(0, Math.max(v.height - h, y));
      this.k = k; this.x = x; this.y = y;
      this.zImg.classList.toggle('mbv-anim', Boolean(anim) && !reduce.matches);
      this.zImg.style.transform = `translate(${x}px,${y}px) scale(${k})`;
      this.zView.classList.toggle('mbv-can-pan', w > v.width + 1 || h > v.height + 1);
    }
    zoomAt(k, cx, cy, anim) { const r = k / this.k; this.set(k, cx - (cx - this.x) * r, cy - (cy - this.y) * r, anim); }
    zoomBy(f) { const v = this.zView.getBoundingClientRect(); this.leaveActual(); this.zoomAt(this.k * f, v.width / 2, v.height / 2, true); }
    gestures() {
      const pts = new Map(); let pinch = null, lastTap = 0;
      const local = e => { const v = this.zView.getBoundingClientRect(); return [e.clientX - v.left, e.clientY - v.top]; };
      this.zView.addEventListener('wheel', e => { e.preventDefault(); this.leaveActual(); const [x, y] = local(e); this.zoomAt(this.k * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0022)), x, y); }, { passive: false });
      this.zView.addEventListener('pointerdown', e => {
        this.zView.setPointerCapture(e.pointerId); pts.set(e.pointerId, local(e));
        if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]), k: this.k }; }
        const now = Date.now();
        if (pts.size === 1 && now - lastTap < 300) { const [x, y] = local(e); this.leaveActual(); this.zoomAt(this.k > this.kFit * 1.3 ? this.kFit : this.kFit * 3, x, y, true); lastTap = 0; } else lastTap = now;
      });
      this.zView.addEventListener('pointermove', e => {
        if (!pts.has(e.pointerId)) return;
        const prev = pts.get(e.pointerId), cur = local(e); pts.set(e.pointerId, cur);
        if (pts.size === 1) this.set(this.k, this.x + cur[0] - prev[0], this.y + cur[1] - prev[1]);
        else if (pinch) { const [a, b] = [...pts.values()]; this.leaveActual(); this.zoomAt(pinch.k * Math.hypot(a[0] - b[0], a[1] - b[1]) / pinch.d, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2); }
      });
      const up = e => { pts.delete(e.pointerId); if (pts.size < 2) pinch = null; };
      this.zView.addEventListener('pointerup', up); this.zView.addEventListener('pointercancel', up);
      this.zView.addEventListener('keydown', e => {
        const s = 60, m = { ArrowLeft: [s, 0], ArrowRight: [-s, 0], ArrowUp: [0, s], ArrowDown: [0, -s] }[e.key];
        if (m) { e.preventDefault(); e.stopPropagation(); this.set(this.k, this.x + m[0], this.y + m[1]); }
      });
    }

    /* ── at actual size: the page drawn at 297 × 210 mm on this screen ── */
    leaveActual() { if (!this.actual) return; this.actual = false; this.zActual.setAttribute('aria-pressed', 'false'); this.zCap.textContent = ''; }
    actualSize(again) {
      if (this.actual && !again) { this.leaveActual(); return this.fit(); }
      const pxmm = readPxmm(); if (!pxmm) return this.calibrate();
      const p = this.zoomPage, v = this.zView.getBoundingClientRect();
      const k = this.data.page.w * pxmm / this.nat.w;
      this.actual = true; this.zActual.setAttribute('aria-pressed', 'true');
      const cx = v.width / 2, cy = v.height / 2;
      this.set(k, cx - (cx - this.x) * (k / this.k), cy - (cy - this.y) * (k / this.k), true);
      const mm = n => (n / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });
      this.zCap.replaceChildren(document.createTextNode(p.real
        ? `Actual size. ${p.real.que} is reproduced here at the size of ${this.data.realOf || 'Mozart’s paper'}, ${mm(p.real.w)} × ${mm(p.real.h)} cm. `
        : `Actual size: the notebook page is ${mm(this.data.page.w * 10)} × ${mm(this.data.page.h * 10)} cm. `));
      const again2 = el('button', 'mbv-link', { type: 'button', text: 'Recalibrate' }); again2.onclick = () => this.calibrate(); this.zCap.append(again2);
    }
    calibrate() {
      let v = readPxmm() || 96 / 25.4 * (devicePixelRatio > 1.5 ? 1.05 : 1);
      const c = this.calib; c.replaceChildren(); c.hidden = false;
      const h = el('h3', '', { text: 'Match a card to your screen' });
      const t = el('p', '', { text: 'Hold any bank card flat against the screen, over the outline. Move the slider until the outline is exactly the size of the card. We only do this once.' });
      const card = el('div', 'mbv-card', { 'aria-hidden': 'true' });
      const range = el('input', 'mbv-range', { type: 'range', min: '2', max: '12', step: '0.01', value: String(v), 'aria-label': 'Size of the outline' });
      const draw = () => { card.style.width = CARD.w * v + 'px'; card.style.height = CARD.h * v + 'px'; };
      range.addEventListener('input', () => { v = +range.value; draw(); });
      const ok = el('button', 'mbv-zbtn mbv-primary', { type: 'button', text: 'It matches' }), no = el('button', 'mbv-zbtn', { type: 'button', text: 'Cancel' });
      ok.onclick = () => { savePxmm(v); c.hidden = true; this.actualSize(true); };
      no.onclick = () => { c.hidden = true; };
      const row = el('div', 'mbv-crow'); row.append(no, ok);
      c.append(h, t, card, range, row); draw(); range.focus();
    }

    /* ── read the text of the page ── */
    openRead() {
      const p = this.zoomPage; if (!p?.read) return;
      const r = this.read; r.replaceChildren();
      const back = el('button', 'mbv-zback', { type: 'button', text: '← Back to the page' }); back.onclick = () => this.closeRead();
      const kick = el('p', 'mbv-rkick', { text: p.read.kick || '' }), h = el('h3', '', { text: p.read.t }), body = el('div', 'mbv-rbody');
      body.innerHTML = p.read.html;
      r.append(back, kick, h, body); r.hidden = false; r.scrollTop = 0; r.focus({ preventScroll: true });
    }
    closeRead() { this.read.hidden = true; this.zRead.focus({ preventScroll: true }); }
  }
  customElements.define('metrica-book-viewer', BookViewer);
})();
